import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import {
  Tabs,
  Table,
  Badge,
  Avatar,
  DropdownMenu,
  Modal,
  Button,
  Input,
  Text,
  Card,
} from '@teals/ui-web';
import type { TabItem, Column } from '@teals/ui-web';
import { useAuth } from '../../hooks/useAuth';
import {
  useGetOrganisationQuery,
  useUpdateOrganisationMutation,
  useGetMembersQuery,
  useInviteMemberMutation,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
  useGetPendingInvitationsQuery,
  useCancelInvitationMutation,
  useGetRolesQuery,
} from '../../store/api/portalApi';
import type { Member, Invitation, Role } from '../../store/api/portalApi';

type BusinessTab = 'members' | 'roles' | 'settings';

const ROLE_VARIANT_MAP: Record<string, 'primary' | 'info' | 'success' | 'neutral'> = {
  owner: 'primary',
  admin: 'info',
  builder: 'success',
  viewer: 'neutral',
};

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1100px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Subtitle = styled.span`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const TabContent = styled.div`
  margin-top: 1rem;
`;

const Section = styled.div`
  margin-top: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.75rem;
`;

const MemberCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  min-height: 44px;
  border: 2px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  color: var(--text-primary);
  background: var(--bg-surface);
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--accent);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const InvitationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const InvitationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const InvitationMeta = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const RolesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
`;

const RoleCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RoleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PermissionList = styled.ul`
  margin: 0;
  padding: 0 0 0 1.25rem;
  list-style: disc;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const PermissionItem = styled.li`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const SettingsForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 480px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  background: var(--bg-danger-subtle, #fef2f2);
  border: 1px solid var(--border-danger, #fecaca);
  border-radius: 0.5rem;
  color: var(--text-danger, #dc2626);
  font-size: 0.875rem;
`;

function getDisplayName(member: Member): string {
  const first = member.firstName || '';
  const last = member.lastName || '';
  const full = `${first} ${last}`.trim();
  return full || member.email || 'Unknown';
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

const BusinessCenter: React.FC = () => {
  const { user } = useAuth();
  const orgId = user?.organizationId;

  const [activeTab, setActiveTab] = useState<BusinessTab>('members');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgDescription, setOrgDescription] = useState('');
  const [page] = useState(1);

  const { data: org, isLoading: orgLoading } = useGetOrganisationQuery(orgId!, {
    skip: !orgId,
  });

  const {
    data: membersData,
    isLoading: membersLoading,
    error: membersError,
  } = useGetMembersQuery({ orgId: orgId!, page, pageSize: 20 }, { skip: !orgId });

  const { data: invitations, isLoading: invitationsLoading } =
    useGetPendingInvitationsQuery(orgId!, { skip: !orgId });

  const { data: roles } = useGetRolesQuery(orgId!, { skip: !orgId });

  const [inviteMember, { isLoading: inviting }] = useInviteMemberMutation();
  const [updateMemberRole] = useUpdateMemberRoleMutation();
  const [removeMember] = useRemoveMemberMutation();
  const [cancelInvitation] = useCancelInvitationMutation();
  const [updateOrganisation, { isLoading: saving }] = useUpdateOrganisationMutation();

  useEffect(() => {
    if (org) {
      setOrgName(org.name || '');
      setOrgDescription('');
    }
  }, [org]);

  useEffect(() => {
    if (roles && roles.length > 0 && !inviteRoleId) {
      const builderRole = roles.find((r) => r.name.toLowerCase() === 'builder');
      setInviteRoleId(builderRole?.id || roles[0].id);
    }
  }, [roles, inviteRoleId]);

  const handleInvite = async () => {
    if (!orgId || !inviteEmail.trim() || !inviteRoleId) return;
    try {
      await inviteMember({ orgId, email: inviteEmail.trim(), roleId: inviteRoleId }).unwrap();
      setInviteOpen(false);
      setInviteEmail('');
    } catch {
      // Error handled by RTK Query cache
    }
  };

  const handleUpdateRole = async (memberId: string, roleId: string) => {
    try {
      await updateMemberRole({ memberId, roleId }).unwrap();
    } catch {
      // Error handled by RTK Query cache
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    try {
      await removeMember(memberId).unwrap();
    } catch {
      // Error handled by RTK Query cache
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId).unwrap();
    } catch {
      // Error handled by RTK Query cache
    }
  };

  const handleSaveSettings = async () => {
    if (!orgId) return;
    try {
      await updateOrganisation({ orgId, body: { name: orgName } }).unwrap();
    } catch {
      // Error handled by RTK Query cache
    }
  };

  const members = membersData?.items || [];
  const totalMembers = membersData?.total || 0;

  const tabs: TabItem[] = useMemo(
    () => [
      { id: 'members', label: 'Members', badge: totalMembers },
      { id: 'roles', label: 'Roles' },
      { id: 'settings', label: 'Settings' },
    ],
    [totalMembers]
  );

  const isLastOwner = (member: Member): boolean => {
    if (!member.roleName || member.roleName.toLowerCase() !== 'owner') return false;
    const ownerCount = members.filter(
      (m) => m.roleName && m.roleName.toLowerCase() === 'owner'
    ).length;
    return ownerCount <= 1;
  };

  const memberColumns: Column<Member>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        width: '30%',
        render: (_val: unknown, row: Member) => (
          <MemberCell>
            <Avatar name={getDisplayName(row)} size="sm" />
            <Text variant="body2" style={{ fontWeight: 500 }}>
              {getDisplayName(row)}
            </Text>
          </MemberCell>
        ),
      },
      {
        key: 'email',
        header: 'Email',
        render: (_val: unknown, row: Member) => <>{row.email || '—'}</>,
      },
      {
        key: 'roleName',
        header: 'Role',
        render: (_val: unknown, row: Member) => {
          const name = row.roleName || '';
          const variant = ROLE_VARIANT_MAP[name.toLowerCase()] ?? 'neutral';
          return (
            <Badge variant={variant} size="sm">
              {name}
            </Badge>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        render: (_val: unknown, row: Member) => (
          <Badge variant={row.status === 'active' ? 'success' : 'neutral'} size="sm">
            {row.status}
          </Badge>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: '48px',
        align: 'right' as const,
        render: (_val: unknown, row: Member) => {
          const locked = isLastOwner(row);
          const roleItems = (roles || [])
            .filter((r) => r.id !== row.roleId)
            .map((r) => ({
              label: `Change to ${r.name}`,
              disabled: locked,
              onClick: () => handleUpdateRole(row.id, r.id),
            }));
          return (
            <DropdownMenu
              align="right"
              items={[
                ...roleItems,
                { divider: true, label: '' },
                {
                  label: 'Remove',
                  variant: 'danger' as const,
                  disabled: locked,
                  onClick: () => handleRemoveMember(row.id),
                },
              ]}
            />
          );
        },
      },
    ],
    [roles, members]
  );

  if (!orgId) {
    return null;
  }

  return (
    <Page>
      <Header>
        <HeaderText>
          <Text variant="h3">Business Center</Text>
          <Subtitle>{orgLoading ? 'Loading...' : org?.name || ''}</Subtitle>
        </HeaderText>
        {activeTab === 'members' && (
          <Button variant="primary" onClick={() => setInviteOpen(true)}>
            Invite Member
          </Button>
        )}
      </Header>

      <Tabs
        items={tabs}
        activeTab={activeTab}
        onChange={(id) => setActiveTab(id as BusinessTab)}
        variant="underline"
      />

      <TabContent>
        {activeTab === 'members' && (
          <>
            {membersError && (
              <ErrorBanner>Failed to load members. Please try again.</ErrorBanner>
            )}
            {membersLoading ? (
              <LoadingContainer>Loading members...</LoadingContainer>
            ) : (
              <Table columns={memberColumns} data={members} rowKey="id" />
            )}

            <Section>
              <SectionTitle>Pending Invitations</SectionTitle>
              <Card padding={0} elevation="sm">
                {invitationsLoading ? (
                  <InvitationRow>
                    <Text variant="body2" color="var(--text-secondary)">
                      Loading invitations...
                    </Text>
                  </InvitationRow>
                ) : !invitations || invitations.length === 0 ? (
                  <InvitationRow>
                    <Text variant="body2" color="var(--text-secondary)">
                      No pending invitations.
                    </Text>
                  </InvitationRow>
                ) : (
                  invitations.map((inv: Invitation) => (
                    <InvitationRow key={inv.id}>
                      <InvitationInfo>
                        <Avatar name={inv.inviteeEmail} size="sm" />
                        <div>
                          <Text variant="body2" style={{ fontWeight: 500 }}>
                            {inv.inviteeEmail}
                          </Text>
                          <InvitationMeta>
                            Sent {formatDate(inv.sentAt)}
                          </InvitationMeta>
                        </div>
                      </InvitationInfo>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelInvitation(inv.id)}
                      >
                        Revoke
                      </Button>
                    </InvitationRow>
                  ))
                )}
              </Card>
            </Section>
          </>
        )}

        {activeTab === 'roles' && (
          <RolesGrid>
            {(roles || []).map((role: Role) => (
              <RoleCard key={role.id} padding={20} elevation="sm">
                <RoleHeader>
                  <Badge
                    variant={ROLE_VARIANT_MAP[role.name.toLowerCase()] ?? 'neutral'}
                    size="md"
                  >
                    {role.name}
                  </Badge>
                  {role.isSystemRole && (
                    <Text variant="caption" color="var(--text-secondary)">
                      System
                    </Text>
                  )}
                </RoleHeader>
                <PermissionList>
                  <PermissionItem>Scope: {role.scope}</PermissionItem>
                </PermissionList>
              </RoleCard>
            ))}
          </RolesGrid>
        )}

        {activeTab === 'settings' && (
          <SettingsForm>
            <Input
              label="Organisation Name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              fullWidth
            />
            <Input
              label="Description"
              value={orgDescription}
              onChange={(e) => setOrgDescription(e.target.value)}
              fullWidth
            />
            <div>
              <Button variant="primary" onClick={handleSaveSettings} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </SettingsForm>
        )}
      </TabContent>

      <Modal isOpen={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite Member">
        <ModalForm>
          <Input
            label="Email Address"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="colleague@company.com"
            fullWidth
          />
          <SelectWrapper>
            <SelectLabel>Role</SelectLabel>
            <Select value={inviteRoleId} onChange={(e) => setInviteRoleId(e.target.value)}>
              {(roles || []).map((r: Role) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </SelectWrapper>
          <ModalActions>
            <Button variant="ghost" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleInvite}
              disabled={!inviteEmail.trim() || !inviteRoleId || inviting}
            >
              {inviting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>
    </Page>
  );
};

export default BusinessCenter;
